import React, { useEffect, useState, useCallback } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus, History, RefreshCw, ChevronLeft, ShieldAlert, Calendar, X } from 'lucide-react';

interface ClosedSignal {
    id: string;
    signal_number: number | null;
    signal_type: string;
    symbol: string | null;
    entry_price: number | null;
    limit_entry: number | null;
    entry_type: string | null;
    stop_loss: number | null;
    sl: number | null;
    take_profit: number | null;
    tp2: number | null;
    result: string;
    status: string | null;
    rr_ratio: number | null;
    rr_at_tp1: number | null;
    rr_ratio_tp2: number | null;
    be_closed: boolean | null;
    strategy_id: string | null;
    strategy_name: string | null;
    closed_at: string | null;
    created_at: string;
}

interface Props {
    session: Session;
}

const SignalHistory: React.FC<Props> = ({ session }) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Which scanner to show — read from URL ?scanner=
    const scannerParam = searchParams.get('scanner') as 'gemini-2.5-pro' | 'elite_53' | null;
    const activeScanner: 'gemini-2.5-pro' | 'elite_53' = scannerParam === 'elite_53' ? 'elite_53' : 'gemini-2.5-pro';

    const [signals, setSignals] = useState<ClosedSignal[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [filter, setFilter] = useState<'all' | 'win' | 'loss' | 'be_closed'>('all');
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 25;

    // Custom date range
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [dateActive, setDateActive] = useState(false);

    // Stats computed from all closed signals for this scanner (within date range if active)
    const [stats, setStats] = useState({ total: 0, wins: 0, losses: 0, beCloses: 0, netRR: 0, winRate: 0 });

    useEffect(() => {
        const checkAccess = async () => {
            const { data: profile } = await supabase
                .from('user_profiles')
                .select('tier, subscription_status, plan_expires_at')
                .eq('id', session.user.id)
                .single();

            if (profile) {
                const notExpired = !profile.plan_expires_at || new Date(profile.plan_expires_at) > new Date();
                const statusOk = !profile.subscription_status || ['active', 'trial'].includes(profile.subscription_status);
                // Signals, Premium, Elite all get access to history
                setHasAccess(['Signals', 'Premium', 'Elite'].includes(profile.tier) && notExpired && statusOk);
            }
        };
        checkAccess();
    }, [session]);

    const fetchSignals = useCallback(async () => {
        setLoading(true);

        // Build date constraints
        const fromDate = dateActive && dateFrom ? new Date(dateFrom).toISOString() : null;
        const toDate = dateActive && dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : null;

        // Fetch ALL closed signals for this scanner (+ date filter) for stats
        let statsQuery = supabase
            .from('signals')
            .select('result, status, rr_ratio, rr_at_tp1, be_closed')
            .eq('scanner_type', activeScanner)
            .in('result', ['win', 'loss', 'invalid']);

        if (fromDate) statsQuery = statsQuery.gte('closed_at', fromDate);
        if (toDate) statsQuery = statsQuery.lte('closed_at', toDate);

        const { data: allData } = await statsQuery;

        if (allData) {
            let netRR = 0;
            let wins = 0;
            let losses = 0;
            let beCloses = 0;

            allData.forEach(s => {
                if (s.result === 'win' && s.status === 'be_closed') {
                    beCloses++;
                    wins++;
                    netRR += typeof s.rr_at_tp1 === 'number' ? s.rr_at_tp1 : 0;
                } else if (s.result === 'win') {
                    wins++;
                    netRR += typeof s.rr_ratio === 'number' ? s.rr_ratio : 0;
                } else if (s.result === 'loss') {
                    losses++;
                    netRR -= 1;
                }
            });

            const total = wins + losses;
            setStats({
                total: allData.length,
                wins,
                losses,
                beCloses,
                netRR: parseFloat(netRR.toFixed(2)),
                winRate: total > 0 ? parseFloat(((wins / total) * 100).toFixed(1)) : 0,
            });
        }

        // Fetch paginated signals for display
        let query = supabase
            .from('signals')
            .select('id, signal_number, signal_type, symbol, entry_price, limit_entry, entry_type, stop_loss, sl, take_profit, tp2, result, status, rr_ratio, rr_at_tp1, rr_ratio_tp2, be_closed, strategy_id, strategy_name, closed_at, created_at')
            .eq('scanner_type', activeScanner)
            .in('result', filter === 'be_closed' ? ['win'] : filter === 'all' ? ['win', 'loss', 'invalid'] : [filter])
            .order('closed_at', { ascending: false })
            .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

        if (filter === 'be_closed') query = query.eq('status', 'be_closed');
        if (fromDate) query = query.gte('closed_at', fromDate);
        if (toDate) query = query.lte('closed_at', toDate);

        const { data } = await query;
        setSignals((data ?? []) as ClosedSignal[]);
        setLoading(false);
    }, [filter, page, activeScanner, dateActive, dateFrom, dateTo]);

    useEffect(() => { if (hasAccess) fetchSignals(); }, [hasAccess, fetchSignals]);
    // Reset page when scanner changes
    useEffect(() => { setPage(0); setFilter('all'); setDateActive(false); setDateFrom(''); setDateTo(''); }, [activeScanner]);

    const applyDateFilter = () => {
        setPage(0);
        setDateActive(true);
    };

    const clearDateFilter = () => {
        setDateFrom('');
        setDateTo('');
        setDateActive(false);
        setPage(0);
    };

    const getOutcome = (sig: ClosedSignal) => {
        if (sig.result === 'win' && sig.status === 'be_closed') return { label: 'BE Closed', sublabel: 'TP1 ✓ → BE SL hit', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20', icon: <Minus className="w-3 h-3" /> };
        if (sig.result === 'win' && sig.tp2) return { label: 'TP2 Hit', sublabel: 'Full target', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: <TrendingUp className="w-3 h-3" /> };
        if (sig.result === 'win') return { label: 'TP1 Hit', sublabel: 'First target', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: <TrendingUp className="w-3 h-3" /> };
        if (sig.result === 'loss') return { label: 'SL Hit', sublabel: '-1R', color: 'text-rose-400 bg-rose-400/10 border-rose-400/20', icon: <TrendingDown className="w-3 h-3" /> };
        return { label: 'Expired', sublabel: 'Invalidated', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20', icon: <Minus className="w-3 h-3" /> };
    };

    const getRR = (sig: ClosedSignal) => {
        if (sig.result === 'win' && sig.status === 'be_closed') return sig.rr_at_tp1 ?? sig.rr_ratio;
        if (sig.result === 'win') return sig.rr_ratio;
        if (sig.result === 'loss') return -1;
        return null;
    };

    if (!hasAccess) {
        return (
            <main className="flex-grow w-full max-w-4xl mx-auto px-4 py-20 text-center">
                <div className="bg-gray-900 border border-gray-800 rounded-3xl p-12">
                    <History className="w-12 h-12 text-finance-accent mx-auto mb-4 opacity-50" />
                    <h2 className="text-2xl font-black text-white mb-3">Signal History — Signals Plan Required</h2>
                    <p className="text-gray-400 mb-8">Upgrade to Signals, Premium or Elite to view the complete signal history with performance analytics.</p>
                    <button onClick={() => navigate('/pricing')} className="bg-finance-accent text-black font-black uppercase tracking-widest px-10 py-4 rounded-xl hover:scale-105 transition-transform">Upgrade Plan</button>
                </div>
            </main>
        );
    }

    const scannerLabel = activeScanner === 'elite_53'
        ? { name: 'Elite 53 Strategies', color: 'text-purple-400', dot: 'bg-purple-400', backPath: '/elite-signals' }
        : { name: 'ICT Agent Army', color: 'text-finance-accent', dot: 'bg-finance-accent', backPath: '/signals' };

    return (
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

            {/* Header */}
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(scannerLabel.backPath)} className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-black text-white flex items-center gap-3">
                            <History className="w-7 h-7 text-finance-accent" />
                            Signal History
                        </h1>
                        <p className={`text-sm mt-0.5 font-bold ${scannerLabel.color}`}>{scannerLabel.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Scanner toggle */}
                    <button
                        onClick={() => setSearchParams({ scanner: 'gemini-2.5-pro' })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeScanner === 'gemini-2.5-pro' ? 'bg-finance-accent text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                        ICT Army
                    </button>
                    <button
                        onClick={() => setSearchParams({ scanner: 'elite_53' })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeScanner === 'elite_53' ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                        Elite 53
                    </button>
                    <button onClick={fetchSignals} className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm px-4 py-2 rounded-lg border border-gray-700 transition-colors">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ── Custom Date Filter ── */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-finance-accent" />
                    <span className="text-xs font-black uppercase tracking-widest text-finance-accent">Custom Date Range</span>
                    {dateActive && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-finance-accent/20 text-finance-accent font-black uppercase">Active</span>
                    )}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">From</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-finance-accent transition-colors"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">To</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-finance-accent transition-colors"
                        />
                    </div>
                    <button
                        onClick={applyDateFilter}
                        disabled={!dateFrom && !dateTo}
                        className="px-5 py-2 bg-finance-accent text-black font-black uppercase tracking-widest text-xs rounded-lg hover:brightness-110 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        Apply Filter
                    </button>
                    {dateActive && (
                        <button
                            onClick={clearDateFilter}
                            className="flex items-center gap-1.5 px-4 py-2 bg-gray-800 text-gray-400 font-black uppercase tracking-widest text-xs rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                        >
                            <X className="w-3 h-3" /> Clear
                        </button>
                    )}
                    {dateActive && dateFrom && dateTo && (
                        <p className="text-[10px] text-gray-500 font-mono ml-1">
                            Showing results from <span className="text-white">{dateFrom}</span> to <span className="text-white">{dateTo}</span>
                        </p>
                    )}
                </div>
            </div>

            {/* Live Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
                {[
                    { label: 'Total Closed',   value: stats.total, color: 'text-white' },
                    { label: 'Wins',           value: stats.wins, color: 'text-emerald-400' },
                    { label: 'Losses',         value: stats.losses, color: 'text-rose-400' },
                    { label: 'BE Closed',      value: stats.beCloses, color: 'text-amber-400' },
                    { label: 'Win Rate',       value: `${stats.winRate}%`, color: stats.winRate >= 50 ? 'text-emerald-400' : 'text-rose-400' },
                    { label: 'Net RR',         value: `${stats.netRR >= 0 ? '+' : ''}${stats.netRR}R`, color: stats.netRR >= 0 ? 'text-emerald-400' : 'text-rose-400' },
                ].map(({ label, value, color }) => (
                    <div key={label} className="bg-gray-900/70 border border-gray-800 rounded-xl p-4 text-center">
                        <p className="text-gray-500 text-[10px] uppercase tracking-widest mb-1.5">{label}</p>
                        <p className={`${color} font-black text-xl font-mono`}>{value}</p>
                    </div>
                ))}
            </div>

            {dateActive && (
                <p className="text-[10px] text-finance-accent/60 font-mono mb-4 italic">
                    ↑ All stats calculated for the selected date range
                </p>
            )}

            {/* Filter Tabs */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
                {[
                    { key: 'all', label: 'All Closed' },
                    { key: 'win', label: 'Wins' },
                    { key: 'loss', label: 'Losses' },
                    { key: 'be_closed', label: 'BE Closed' },
                ].map(({ key, label }) => (
                    <button key={key} onClick={() => { setFilter(key as any); setPage(0); }}
                        className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${filter === key ? 'bg-finance-accent text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                        {label}
                    </button>
                ))}
                <span className="ml-auto text-xs text-gray-600">Page {page + 1}</span>
            </div>

            {/* Signals Table */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="py-20 text-center text-gray-600">
                        <div className="w-8 h-8 border-4 border-finance-accent border-r-transparent rounded-full animate-spin mx-auto mb-4" />
                        Loading signal history...
                    </div>
                ) : signals.length === 0 ? (
                    <div className="py-20 text-center text-gray-600">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No signals found for this filter{dateActive ? ' in the selected date range' : ''}.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[800px]">
                            <thead className="bg-black/30 text-gray-500 text-[10px] uppercase tracking-widest border-b border-gray-800">
                                <tr>
                                    <th className="px-5 py-3 text-left font-black">#</th>
                                    <th className="px-5 py-3 text-left font-black">Signal</th>
                                    <th className="px-5 py-3 text-left font-black">Entry / SL / TP</th>
                                    <th className="px-5 py-3 text-left font-black">Outcome</th>
                                    <th className="px-5 py-3 text-center font-black">R:R</th>
                                    <th className="px-5 py-3 text-left font-black">Strategy</th>
                                    <th className="px-5 py-3 text-left font-black">Closed</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800/60">
                                {signals.map((sig) => {
                                    const outcome = getOutcome(sig);
                                    const rr = getRR(sig);
                                    const isBuy = sig.signal_type === 'BUY';
                                    const entryPrice = sig.limit_entry ?? sig.entry_price;

                                    return (
                                        <tr key={sig.id} className="hover:bg-white/[0.02] transition-all">
                                            <td className="px-5 py-4">
                                                <span className="text-gray-500 font-mono">#{sig.signal_number || '—'}</span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${isBuy ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                                                    <span className="font-black text-white">{sig.symbol || 'XAUUSD'}</span>
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-black border ${isBuy ? 'text-emerald-400 border-emerald-400/20 bg-emerald-400/5' : 'text-rose-400 border-rose-400/20 bg-rose-400/5'}`}>
                                                        {sig.signal_type} {sig.entry_type && sig.entry_type !== 'MARKET' ? `(${sig.entry_type})` : ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 font-mono text-[10px] space-y-0.5 text-gray-400">
                                                <div>E: <span className="text-white">{entryPrice ?? '—'}</span></div>
                                                <div>SL: <span className="text-rose-400">{sig.sl ?? sig.stop_loss ?? '—'}</span></div>
                                                <div>TP: <span className="text-emerald-400">{sig.take_profit ?? '—'}{sig.tp2 ? ` / ${sig.tp2}` : ''}</span></div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className={`inline-flex items-center gap-1 text-[9px] px-2 py-1 rounded-full border font-black uppercase tracking-widest ${outcome.color}`}>
                                                    {outcome.icon} {outcome.label}
                                                </span>
                                                <p className="text-gray-600 text-[9px] mt-0.5">{outcome.sublabel}</p>
                                                {sig.rr_at_tp1 && sig.status === 'be_closed' && (
                                                    <p className="text-amber-400/70 text-[9px] mt-0.5 font-mono">TP1 locked: +{sig.rr_at_tp1.toFixed(2)}R</p>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-center">
                                                <span className={`font-black font-mono text-sm ${rr === null ? 'text-gray-600' : rr > 0 ? 'text-emerald-400' : rr < 0 ? 'text-rose-400' : 'text-amber-400'}`}>
                                                    {rr === null ? '—' : `${rr > 0 ? '+' : ''}${typeof rr === 'number' ? rr.toFixed(2) : rr}R`}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-gray-500 text-[10px]">
                                                {sig.strategy_name ?? sig.strategy_id ?? '—'}
                                            </td>
                                            <td className="px-5 py-4 text-gray-500 text-[10px] whitespace-nowrap">
                                                {sig.closed_at
                                                    ? new Date(sig.closed_at).toLocaleString([], { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                                                    : '—'
                                                }
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
                <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
                    className="px-4 py-2 rounded-lg bg-gray-800 text-gray-400 text-xs font-black uppercase disabled:opacity-30 hover:bg-gray-700 transition-colors">
                    ← Previous
                </button>
                <span className="text-gray-600 text-xs">Page {page + 1} · {signals.length} results</span>
                <button disabled={signals.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)}
                    className="px-4 py-2 rounded-lg bg-gray-800 text-gray-400 text-xs font-black uppercase disabled:opacity-30 hover:bg-gray-700 transition-colors">
                    Next →
                </button>
            </div>

            {/* Risk Footer */}
            <div className="mt-12 bg-red-500/5 border border-red-500/10 rounded-2xl p-5 flex items-center gap-4">
                <ShieldAlert className="w-6 h-6 text-red-500 shrink-0" />
                <p className="text-gray-500 text-xs leading-relaxed">
                    Past performance is not indicative of future results. These signals are for informational purposes only and do not constitute financial advice.
                </p>
            </div>
        </main>
    );
};

export default SignalHistory;
